// TMOS User Approval Stats Component
// Date: December 19, 2025
// Purpose: Display approval statistics dashboard

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ApprovalStats } from '@/services/userApprovalService';
import { Card } from '@/components/ui/Card';
import { CheckCircleIcon, XCircleIcon, ClockIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface UserApprovalStatsProps {
  stats: ApprovalStats;
}

export const UserApprovalStats: React.FC<UserApprovalStatsProps> = ({ stats }) => {
  const { t } = useLanguage();

  const statCards = [
    {
      label: t('userApproval.stats.pending') || 'Pending',
      value: stats.pending,
      icon: ClockIcon,
      color: 'yellow',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600',
      iconColor: 'text-yellow-500'
    },
    {
      label: t('userApproval.stats.approved') || 'Approved',
      value: stats.approved,
      icon: CheckCircleIcon,
      color: 'green',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      iconColor: 'text-green-500'
    },
    {
      label: t('userApproval.stats.rejected') || 'Rejected',
      value: stats.rejected,
      icon: XCircleIcon,
      color: 'red',
      bgColor: 'bg-red-50',
      textColor: 'text-red-600',
      iconColor: 'text-red-500'
    },
    {
      label: t('userApproval.stats.total') || 'Total',
      value: stats.total,
      icon: ArrowPathIcon,
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      iconColor: 'text-blue-500'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {statCards.map((stat) => {
        const Icon = stat.icon;
        return (
          <Card key={stat.label} className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className={`text-2xl font-bold ${stat.textColor} mt-1`}>
                  {stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-full ${stat.bgColor}`}>
                <Icon className={`h-6 w-6 ${stat.iconColor}`} />
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

